import type { ConnectionType, NetworkNode, NodeKind } from "@/features/network-topology/types";

/** 接続開始 → online 遷移までの擬似遅延 (ms) */
export const CONNECTING_DELAY_MS = 2000;

/** 接続方式のメタデータ (UI 表示用) */
export interface ConnectionTypeMeta {
  /** バッジ表示の短い名前 */
  label: string;
  /** 詳細説明（パネル表示用） */
  description: string;
  /** チップ背景色 */
  color: string;
}

export const CONNECTION_TYPE_META: Record<ConnectionType, ConnectionTypeMeta> = {
  rj45: {
    label: "RJ45",
    description: "Ethernet 銅線 (Cat5e/6/6A/7)",
    color: "#1976d2",
  },
  sfp: {
    label: "SFP",
    description: "1 GbE SFP モジュール",
    color: "#7b1fa2",
  },
  sfp_plus: {
    label: "SFP+",
    description: "10 GbE SFP+ モジュール",
    color: "#6a1b9a",
  },
  fiber: {
    label: "Fiber",
    description: "光ファイバ (LC コネクタ)",
    color: "#0288d1",
  },
  coax: {
    label: "Coax",
    description: "同軸ケーブル",
    color: "#5d4037",
  },
  serial: {
    label: "Serial",
    description: "RS-232 / RS-485 シリアル",
    color: "#558b2f",
  },
  wifi: {
    label: "Wi-Fi",
    description: "無線 LAN (IEEE 802.11)",
    color: "#ef6c00",
  },
  usb: {
    label: "USB",
    description: "USB ケーブル",
    color: "#37474f",
  },
};

/** 新規ノード追加時のデフォルト接続方式 */
export const DEFAULT_CONNECTION_TYPE: ConnectionType = "rj45";

/** Key Events ログの最大保持件数 */
export const MAX_LOG_EVENTS = 50;

/** 表示用のノード種別メタデータ */
export interface NodeKindMeta {
  /** UI 上の総称（"PLC", "L2 Switch" 等） */
  displayName: string;
  /** どの kind を子として持てるか（参考。現状は強制ルールなし） */
  allowedChildren: readonly NodeKind[];
  /** root チェーンとして固定でデフォルト存在する想定か（情報用） */
  defaultRootChain: boolean;
}

export const NODE_KIND_META: Record<NodeKind, NodeKindMeta> = {
  internet: {
    displayName: "Internet",
    allowedChildren: ["gateway"],
    defaultRootChain: true,
  },
  gateway: {
    displayName: "Gateway",
    allowedChildren: ["l3_switch", "l2_switch"],
    defaultRootChain: true,
  },
  l3_switch: {
    displayName: "L3 Switch",
    allowedChildren: ["l3_switch", "l2_switch", "server"],
    defaultRootChain: true,
  },
  l2_switch: {
    displayName: "L2 Switch",
    allowedChildren: ["l2_switch", "server", "plc", "robot_controller", "agv_controller", "sensor"],
    defaultRootChain: false,
  },
  server: {
    displayName: "Server",
    allowedChildren: [],
    defaultRootChain: false,
  },
  plc: {
    displayName: "PLC",
    allowedChildren: ["sensor"],
    defaultRootChain: false,
  },
  robot_controller: {
    displayName: "Robot Controller",
    allowedChildren: [],
    defaultRootChain: false,
  },
  agv_controller: {
    displayName: "AGV Controller",
    allowedChildren: [],
    defaultRootChain: false,
  },
  sensor: {
    displayName: "Sensor",
    allowedChildren: [],
    defaultRootChain: false,
  },
};

/**
 * デフォルトの工場ネットワークトポロジ。
 *
 * 構造:
 *   internet
 *     └─ gw
 *         └─ l3-core
 *             ├─ l2-line-a
 *             │   ├─ srv-mes      (server)
 *             │   ├─ plc-press    (PLC, sim: press)
 *             │   ├─ rbc-arm      (robot_controller, sim: arm)
 *             │   └─ agv-tr       (agv_controller, sim: transport)
 *             └─ l2-sensors
 *                 ├─ sensor-1, 2, 3
 *                 └─ srv-spawn   (server, sim: spawn = MES の投入指示)
 */
export const DEFAULT_TOPOLOGY: NetworkNode[] = [
  {
    id: "internet",
    kind: "internet",
    label: "Internet",
    parentId: null,
    manual: "online",
    simBinding: null,
  },
  {
    id: "gw",
    kind: "gateway",
    label: "GW",
    parentId: "internet",
    manual: "online",
    simBinding: null,
    linkType: "fiber",
  },
  {
    id: "l3-core",
    kind: "l3_switch",
    label: "L3-Core",
    parentId: "gw",
    manual: "online",
    simBinding: null,
    linkType: "sfp_plus",
  },
  {
    id: "l2-line-a",
    kind: "l2_switch",
    label: "L2-LineA",
    parentId: "l3-core",
    manual: "online",
    simBinding: null,
    linkType: "sfp_plus",
  },
  {
    id: "srv-mes",
    kind: "server",
    label: "MES",
    parentId: "l2-line-a",
    manual: "online",
    simBinding: null,
    linkType: "rj45",
  },
  {
    id: "plc-press",
    kind: "plc",
    label: "PLC-Press",
    parentId: "l2-line-a",
    manual: "online",
    simBinding: { kind: "press" },
    linkType: "rj45",
  },
  {
    id: "rbc-arm",
    kind: "robot_controller",
    label: "Robot-Arm",
    parentId: "l2-line-a",
    manual: "online",
    simBinding: { kind: "arm" },
    linkType: "rj45",
  },
  {
    id: "agv-tr",
    kind: "agv_controller",
    label: "AGV-Ctrl",
    parentId: "l2-line-a",
    manual: "online",
    simBinding: { kind: "transport" },
    linkType: "wifi",
  },
  {
    id: "l2-sensors",
    kind: "l2_switch",
    label: "L2-Sensors",
    parentId: "l3-core",
    manual: "online",
    simBinding: null,
    linkType: "rj45",
  },
  {
    id: "sensor-1",
    kind: "sensor",
    label: "Sensor-1",
    parentId: "l2-sensors",
    manual: "online",
    simBinding: null,
    linkType: "serial",
  },
  {
    id: "sensor-2",
    kind: "sensor",
    label: "Sensor-2",
    parentId: "l2-sensors",
    manual: "online",
    simBinding: null,
    linkType: "rj45",
  },
  {
    id: "srv-spawn",
    kind: "server",
    label: "MES-Spawn",
    parentId: "l2-sensors",
    manual: "online",
    simBinding: { kind: "spawn" },
    linkType: "rj45",
  },
];
